import React, {useEffect, useRef} from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

interface QuillEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    modules?: Record<string, any>;
    formats?: string[];
    readOnly?: boolean;
}

const QuillEditor: React.FC<QuillEditorProps> = ({
    value,
    onChange,
    placeholder,
    modules,
    formats,
    readOnly,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const onChangeRef = useRef(onChange);
    const isInternalChange = useRef(false);
    const initialValueRef = useRef(value);

    onChangeRef.current = onChange;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Prevent double init (React StrictMode)
        if (quillRef.current) return;

        const editorDiv = document.createElement('div');
        container.appendChild(editorDiv);

        const quill = new Quill(editorDiv, {
            theme: 'snow',
            // No placeholder in read-only mode: content is set via innerHTML (bypassing
            // Quill's model), so the ql-blank placeholder would otherwise overlap the content.
            placeholder: readOnly ? undefined : placeholder,
            readOnly,
            modules: modules || {
                // No formatting toolbar in read-only mode.
                toolbar: readOnly ? false : [
                    [{header: [1, 2, 3, false]}],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{list: 'ordered'}, {list: 'bullet'}],
                    ['blockquote'],
                    [{align: []}, {color: []}],
                    [{script: 'sub'}, {script: 'super'}],
                    ['clean'],
                ],
            },
            formats,
        });

        if (initialValueRef.current) {
            quill.root.innerHTML = initialValueRef.current;
        }

        quill.on('text-change', () => {
            isInternalChange.current = true;
            onChangeRef.current(quill.root.innerHTML);
        });

        quillRef.current = quill;

        return () => {
            quill.off('text-change');
            quillRef.current = null;
            container.innerHTML = '';
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Toggle editability when readOnly changes
    useEffect(() => {
        if (quillRef.current) {
            quillRef.current.enable(!readOnly);
        }
    }, [readOnly]);

    // Sync external value changes
    useEffect(() => {
        if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
        }
        if (quillRef.current && quillRef.current.root.innerHTML !== value) {
            quillRef.current.root.innerHTML = value || '';
        }
    }, [value]);

    return <div ref={containerRef} />;
};

export default QuillEditor;
